import {
  FlightsSearchFormDateFieldType,
  FlightsSearchFormTravelersOption,
  FlightsTravelerAgeSelector,
  TravelerOptionItem
} from 'types/search/wizard';
import { FlightsTravelerType } from '__generated__/api/types';

const LOCATION_MIN_CHARACTER_LENGTH = 3;
const INVALID_SPECIAL_CHARACTERS_REGEX = /.*[~!\"@#$%^*+=|{}:;\[\]?]+.*/; // TODO: special character set should prob be returned from bex-api
const VALID_DATE_FORMAT = /^(-?(?:[1-9][0-9]*)?[0-9]{4})-(1[0-2]|0[1-9])-(3[01]|0[1-9]|[12][0-9])$/;
const MILLISECONDS_IN_A_DAY = 24 * 60 * 60 * 1000;
const MAX_NUMBER_OF_TRAVELERS = 6;
const MIN_NUMBER_OF_ADULTS = 1;
const MIN_AGE_TO_ACCOMPANY_INFANTS = 12;

const isAgeValid = (age: FlightsTravelerAgeSelector, minAge: number) => {
  const selectedAgeItem = age.items.find(item => item.selected);

  if (!selectedAgeItem) {
    return false;
  }

  return selectedAgeItem.value >= minAge;
};

const validateDifferentLocations = (locationA: string, locationB: string): boolean => locationA !== locationB;

const validateLocationMinimumLength = (location: string): boolean => location.length >= LOCATION_MIN_CHARACTER_LENGTH;

const validateLocationWithoutSpecialCharacters = (location: string): boolean =>
  location.match(INVALID_SPECIAL_CHARACTERS_REGEX) === null;

const validateLocationNotEmpty = (location: string): boolean => location && location.length !== 0;

const validateDateFormat = ({ date }: FlightsSearchFormDateFieldType): boolean =>
  date && date.day && date.month && date.year && VALID_DATE_FORMAT.test(date.isoFormat);

const validateDateRange = ({ date, daysBookableInAdvance }: FlightsSearchFormDateFieldType): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const selectedDate = new Date(date.year, date.month - 1, date.day);

  const diffInDays = (selectedDate.getTime() - today.getTime()) / MILLISECONDS_IN_A_DAY;

  return diffInDays <= daysBookableInAdvance;
};

const validateDateIsNotBeforePreviousDate = (
  current: FlightsSearchFormDateFieldType,
  previous: FlightsSearchFormDateFieldType
): boolean => {
  if (previous) {
    const currentDate = new Date(current.date.isoFormat);
    const previousDate = new Date(previous.date.isoFormat);

    return previousDate <= currentDate;
  }

  return true;
};

const validateMaxDuration = (
  startDate: FlightsSearchFormDateFieldType,
  endDate: FlightsSearchFormDateFieldType
): boolean => {
  if (startDate && endDate) {
    const selectedStartDate = new Date(startDate.date.isoFormat);
    const selectedEndDate = new Date(endDate.date.isoFormat);
    const diffInDays = (selectedEndDate.getTime() - selectedStartDate.getTime()) / MILLISECONDS_IN_A_DAY;

    return diffInDays <= startDate.maxStayDurationAllowed;
  }

  return false;
};

const validateDateIsAfterDefaultDate = (
  current: FlightsSearchFormDateFieldType,
  defaultSelectedDate: FlightsSearchFormDateFieldType
): boolean => {
  if (defaultSelectedDate && current) {
    const currentDate = new Date(current.date.isoFormat);
    const defaultDate = new Date(defaultSelectedDate.date.isoFormat);

    return currentDate >= defaultDate;
  }

  return false;
};

const validateTravelerCount = ({ items }: FlightsSearchFormTravelersOption): boolean => {
  const adultCount = items.find(item => item.type === FlightsTravelerType.ADULT).travelerCount;
  const childCount = items.find(item => item.type === FlightsTravelerType.CHILD).travelerCount;
  const infantCount = items.find(
    item => item.type === FlightsTravelerType.INFANT_IN_LAP || item.type === FlightsTravelerType.INFANT_IN_SEAT
  ).travelerCount;

  const totalTravelerCount = adultCount + childCount + infantCount;

  return totalTravelerCount <= MAX_NUMBER_OF_TRAVELERS && totalTravelerCount > 0;
};

const validateAdultCount = ({ items }: FlightsSearchFormTravelersOption): boolean => {
  const adultCount = items.find(item => item.type === FlightsTravelerType.ADULT).travelerCount;

  return adultCount >= MIN_NUMBER_OF_ADULTS && adultCount <= MAX_NUMBER_OF_TRAVELERS;
};

const validateInfantsInLap = ({ items }: FlightsSearchFormTravelersOption): boolean => {
  const adultCount = items.find(item => item.type === FlightsTravelerType.ADULT).travelerCount;
  const childrenAges = items.find(item => item.type === FlightsTravelerType.CHILD).travelerAges || [];
  const infantsInLap = items.find(item => item.type === FlightsTravelerType.INFANT_IN_LAP);
  const infantsInLapCount = infantsInLap ? infantsInLap.travelerCount : 0;
  const childrenAllowedToHaveInfantsInLap =
    childrenAges.filter(age => isAgeValid(age, MIN_AGE_TO_ACCOMPANY_INFANTS)).length || 0;

  return adultCount + childrenAllowedToHaveInfantsInLap >= infantsInLapCount;
};

const validateInfantsInSeat = ({ items }: FlightsSearchFormTravelersOption): boolean => {
  const adultCount = items.find(item => item.type === FlightsTravelerType.ADULT).travelerCount;
  const childrenAges = items.find(item => item.type === FlightsTravelerType.CHILD).travelerAges || [];
  const childrenOldEnoughToAccompanyInfants =
    childrenAges.filter(age => isAgeValid(age, MIN_AGE_TO_ACCOMPANY_INFANTS)).length || 0;
  const infantsInSeat = items.find(item => item.type === FlightsTravelerType.INFANT_IN_SEAT);
  const infantsInSeatCount = infantsInSeat ? infantsInSeat.travelerCount : 0;
  const travelersOldEnoughToAccompanyInfants = adultCount + childrenOldEnoughToAccompanyInfants;

  return infantsInSeatCount <= travelersOldEnoughToAccompanyInfants * 2;
};

const validateAges = (travelerOption: TravelerOptionItem): boolean => {
  const travelerCount = travelerOption ? travelerOption.travelerCount : 0;
  const travelerAges =
    travelerOption && travelerOption.travelerAges ? travelerOption.travelerAges.filter(age => isAgeValid(age, 0)) : [];

  return travelerAges.length === travelerCount;
};

const validateChildAges = ({ items }: FlightsSearchFormTravelersOption): boolean => {
  const children = items.find(item => item.type === FlightsTravelerType.CHILD);

  return validateAges(children);
};

const validateInfantAges = ({ items }: FlightsSearchFormTravelersOption): boolean => {
  const infants = items.find(
    item => item.type === FlightsTravelerType.INFANT_IN_LAP || item.type === FlightsTravelerType.INFANT_IN_SEAT
  );

  return validateAges(infants);
};

export const InputValidator = {
  LOCATION: {
    TOO_SHORT_AIRPORT_STRING: validateLocationMinimumLength,
    SPECIAL_CHARACTER_NOT_ALLOWED: validateLocationWithoutSpecialCharacters,
    LOCATION_SAME: validateDifferentLocations,
    LOCATION_EMPTY: validateLocationNotEmpty
  },
  DATE: {
    INVALID_DATE_FORMAT: validateDateFormat,
    DATE_NOT_IN_VALID_RANGE: validateDateRange,
    DATE_BEFORE_PREVIOUS_DATE: validateDateIsNotBeforePreviousDate,
    DATE_NOT_IN_VALID_RANGE_TO_DEFAULT_DATE: validateDateIsAfterDefaultDate,
    MAX_STAY_DURATION_ERROR: validateMaxDuration
  },
  TRAVELER: {
    INVALID_TRAVELER_COUNT: validateTravelerCount,
    LESS_THAN_MINIMUM_ADULT: validateAdultCount,
    UNATTENDED_INFANT_IN_LAP: validateInfantsInLap,
    UNATTENDED_INFANT_IN_SEAT: validateInfantsInSeat,
    CHILD_AGE_MISSING: validateChildAges,
    INFANT_AGE_MISSING: validateInfantAges
  }
};
